import { get, post } from '../api.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { setEmail, isLimited } from '../auth-store.js';
import { navigate } from '../router.js';

/**
 * /account — limited-session landing page.
 *
 * Reached after a magic-link login (Limited JWT). The user has no vault
 * key on the client; the server rejects item/grant/etc. calls for this
 * session. Surface the handful of actions that *are* available, plus a
 * clear "Unlock vault" call-to-action.
 */
export async function renderAccount(container) {
  setTopbar('Account', '');
  container.innerHTML = '<p class="loading">Loading...</p>';

  let me;
  try {
    me = await get('/auth/me');
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message || String(err))}</div>`;
    return;
  }

  if (me.email) setEmail(me.email);
  const limited = isLimited();
  const pending = me.pending_account_deletion;
  const deadmanEnabled = !!me.deadman_enabled;
  const limitedCheckinAllowed = !!me.limited_deadman_checkin_enabled;

  let html = '';

  // State banner. Explain *why* this surface is pared down.
  if (limited) {
    html += `
      <div class="msg msg-warning" style="margin-bottom:16px">
        <strong>Your vault is locked.</strong> You signed in with a magic link, so we can't decrypt your vault items here. Use "Unlock vault" to enter your password and get full access.
      </div>`;
  }

  // Pending-deletion warning — applies to any session.
  if (pending) {
    const when = new Date(pending.scheduled_for).toLocaleString();
    html += `
      <div class="msg msg-error" style="margin-bottom:16px">
        <strong>Account deletion scheduled for ${esc(when)}.</strong>
        Signing in with your password will cancel the deletion automatically. If this wasn't you, cancel it now — check your email for a cancellation link.
      </div>`;
  }

  html += '<div class="section-title">Account</div>';
  html += `
    <div class="card">
      <div class="detail-label">Email</div>
      <div class="detail-value">${esc(me.email)}</div>
      <div class="detail-label">Account created</div>
      <div class="detail-value">${new Date(me.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
    </div>`;

  html += '<div class="section-title" style="margin-top:24px">Actions</div>';
  html += '<div style="display:flex;flex-direction:column;gap:10px">';

  html += tile({
    icon: 'lock-open',
    title: 'Unlock vault',
    body: 'Enter your password to decrypt your vault and use all features.',
    href: '#/unlock',
    variant: 'primary',
  });

  html += tile({
    icon: 'fingerprint',
    title: 'Passkeys',
    // Adding a passkey requires Full (server blocks /passkeys/register/*
    // from Limited). Limited users can still list and revoke credentials.
    body: limited
      ? 'View or revoke passkeys on this account. Adding new passkeys requires unlocking the vault.'
      : 'Add, remove, or manage WebAuthn passkeys on this account.',
    href: '#/settings',
  });

  html += tile({
    icon: 'life-buoy',
    title: 'Recover with mnemonic',
    body: 'Lost your password? Use your 12-word recovery words to set a new one.',
    href: '#/recover',
  });

  html += tile({
    icon: 'credit-card',
    title: 'Billing',
    body: 'View your storage balance and billing history.',
    href: '#/billing',
  });

  if (deadmanEnabled && limitedCheckinAllowed) {
    html += tile({
      icon: 'activity',
      title: 'Check in (dead man’s switch)',
      body: 'Reset the inactivity timer. You enabled check-in from magic-link-only sessions.',
      id: 'deadman-checkin-btn',
    });
  }

  html += tile({
    icon: 'trash-2',
    title: 'Close account',
    body: limited
      ? `Initiate account deletion. Because you're signed in with a magic link, deletion is queued for 7 days and any password sign-in cancels it.`
      : 'Permanently delete your account and all vault data.',
    href: '#/close-account',
    variant: 'danger',
  });

  html += '</div>';

  container.innerHTML = html;
  lucideIcons();

  const checkinBtn = document.getElementById('deadman-checkin-btn');
  if (checkinBtn) {
    checkinBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      checkinBtn.setAttribute('aria-busy', 'true');
      try {
        await post('/deadman/checkin', {});
        checkinBtn.outerHTML = '<div class="msg">Check-in recorded.</div>';
        lucideIcons();
      } catch (err) {
        checkinBtn.insertAdjacentHTML(
          'afterend',
          `<div class="msg msg-error">${esc(err.message || String(err))}</div>`
        );
        checkinBtn.removeAttribute('aria-busy');
      }
    });
  }
}

function tile({ icon, title, body, href, id, variant }) {
  const variantClass = variant === 'danger'
    ? 'border-color:var(--danger)'
    : variant === 'primary'
      ? 'border-color:var(--accent)'
      : '';
  const style = variantClass ? ` style="${variantClass}"` : '';
  const inner = `
    <div style="display:flex;gap:12px;align-items:flex-start">
      <div style="flex-shrink:0;margin-top:2px"><i data-lucide="${icon}"></i></div>
      <div style="flex:1">
        <div style="font-weight:600;margin-bottom:4px">${esc(title)}</div>
        <div style="color:var(--text-secondary);font-size:13px">${esc(body)}</div>
      </div>
      <div style="flex-shrink:0;margin-top:2px"><i data-lucide="chevron-right"></i></div>
    </div>`;
  if (href) {
    return `<a class="card" href="${href}"${style} style="text-decoration:none;color:inherit;${variantClass}">${inner}</a>`;
  }
  return `<button type="button" id="${id}" class="card"${style} style="text-align:left;background:transparent;cursor:pointer;color:inherit;${variantClass}">${inner}</button>`;
}
